import { Injectable, InjectionToken, WritableSignal, inject, signal } from '@angular/core';
import { Params } from '@angular/router';
import { Subscription, take } from 'rxjs';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { WindowRefService } from '@services/window-ref-service';
import { CheckoutService } from '@services/checkout-service';
import { DeliveryDateInfo } from '@services/checkout-service/checkout.service';
import { extractArea, extractPincode } from 'client/helper/geo-address-helper/geo-address-helper';
import { formatPlaceName } from 'client/helper/address-helper/address-helper';
import type { AddressAutofillHandoff } from './address-autofill-handoff.service';

export enum DetectionState { Idle = 'idle', Detecting = 'detecting', Detected = 'detected', Failed = 'failed' }
export enum DetectFailure { PermissionDenied = 'permission_denied', NotFound = 'not_found', Timeout = 'timeout', Cancelled = 'cancelled' }
export enum AutofillFocus { Building = 'building', Area = 'area' }

export interface LatLong { latitude: number; longitude: number; }
export interface DetectedLocation { area: string | null; pincode: number | null; city: string; state: string; }
export interface DetectOptions { withDelivery: boolean; languagePreference: string; }
export interface DetectOutcome { detected: boolean; reason?: DetectFailure; }

type CoordinatesResult = LatLong | { reason: DetectFailure };

export const ADDRESS_AUTOFILL_TIMEOUT_MS: InjectionToken<number> = new InjectionToken<number>(
  'ADDRESS_AUTOFILL_TIMEOUT_MS',
  { factory: (): number => 15000 },
);

@Injectable()
export class AddressAutofillService {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  private readonly windowRef: WindowRefService = inject(WindowRefService);
  private readonly checkoutService: CheckoutService = inject(CheckoutService);
  private readonly timeoutMs: number = inject(ADDRESS_AUTOFILL_TIMEOUT_MS);

  readonly state: WritableSignal<DetectionState> = signal(DetectionState.Idle);
  readonly location: WritableSignal<DetectedLocation | null> = signal(null);
  readonly delivery: WritableSignal<DeliveryDateInfo | null> = signal(null);
  readonly autofilled: WritableSignal<boolean> = signal(false);
  coordinates: LatLong | null = null;
  reverseGeocodeResponse: unknown = null;

  private cancelPending: (() => void) | null = null;
  private generation: number = 0;

  async detect(options: DetectOptions): Promise<DetectOutcome> {
    const run = ++this.generation;
    this.state.set(DetectionState.Detecting);
    this.location.set(null);
    this.delivery.set(null);

    const coordinates = await this.waitForCoordinates();
    if ('reason' in coordinates) return this.fail(run, coordinates.reason);

    const response = await this.reverseGeocode(coordinates);
    if (!this.isCurrent(run)) return this.cancelled();
    const area = extractArea(response) || null;
    const pincode = extractPincode(response) || null;
    if (!area && !pincode) return this.fail(run, DetectFailure.NotFound);

    const place = pincode ? await this.fetchPlace(pincode) : { city: '', state: '' };
    if (options.withDelivery && pincode) {
      this.delivery.set(await this.checkoutService.fetchDeliveryDateInfo(String(pincode), options.languagePreference));
    }
    if (!this.isCurrent(run)) return this.cancelled();

    this.coordinates = coordinates;
    this.reverseGeocodeResponse = response;
    this.location.set({ area, pincode, ...place });
    this.state.set(DetectionState.Detected);
    return { detected: true };
  }

  cancel(): void {
    if (this.state() !== DetectionState.Detecting) return;
    this.generation++;
    this.state.set(DetectionState.Idle);
    this.cancelPending?.();
  }

  restore(handoff: AddressAutofillHandoff): void {
    this.location.set(handoff.location);
    this.coordinates = handoff.coordinates;
    this.reverseGeocodeResponse = handoff.reverseGeocodeResponse;
    this.state.set(DetectionState.Detected);
    this.autofilled.set(true);
  }

  confirmAddressUrl(queryParams: Params): string {
    const params = this.checkoutService.buildPreservedAddressParams(queryParams, { fromLocationAutofill: 'true' });
    return this.checkoutService.buildUrlWithParams('/user/checkout', params);
  }

  clear(): void {
    this.state.set(DetectionState.Idle);
    this.location.set(null);
    this.delivery.set(null);
    this.autofilled.set(false);
    this.coordinates = null;
    this.reverseGeocodeResponse = null;
  }

  private isCurrent(run: number): boolean {
    return run === this.generation && this.state() === DetectionState.Detecting;
  }

  private fail(run: number, reason: DetectFailure): DetectOutcome {
    if (reason === DetectFailure.Cancelled || !this.isCurrent(run)) return this.cancelled();
    this.state.set(DetectionState.Failed);
    return { detected: false, reason };
  }

  private cancelled(): DetectOutcome {
    return { detected: false, reason: DetectFailure.Cancelled };
  }

  private async reverseGeocode(coordinates: LatLong): Promise<unknown> {
    try {
      return await this.conn.getDetailedLocationFromCoordinates({
        latitude: String(coordinates.latitude),
        longitude: String(coordinates.longitude),
      });
    } catch {
      return null;
    }
  }

  private async fetchPlace(pincode: number): Promise<{ city: string; state: string }> {
    try {
      const pinCodeData = await this.conn.getPinCodeInfo(pincode);
      return { city: formatPlaceName(pinCodeData?.district || ''), state: formatPlaceName(pinCodeData?.state || '') };
    } catch {
      return { city: '', state: '' };
    }
  }

  private waitForCoordinates(): Promise<CoordinatesResult> {
    return new Promise<CoordinatesResult>((resolve: (value: CoordinatesResult) => void): void => {
      let settled = false;
      let nativeUpdates: Subscription | null = null;
      const settle = (value: CoordinatesResult): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        nativeUpdates?.unsubscribe();
        this.cancelPending = null;
        resolve(value);
      };
      const timer = setTimeout((): void => settle({ reason: DetectFailure.Timeout }), this.timeoutMs);
      this.cancelPending = (): void => settle({ reason: DetectFailure.Cancelled });

      nativeUpdates = this.broadcast.on<{ [key: string]: string }>('LOCATION_UPDATE').pipe(take(1))
        .subscribe((latLong: { [key: string]: string }): void => settle({
          latitude: Number(latLong.latitude),
          longitude: Number(latLong.longitude),
        }));
      if (this.appWebBridge.getLocationDetails()) return;

      nativeUpdates.unsubscribe();
      this.windowRef.nativeWindow.navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition): void => settle({ latitude: position.coords.latitude, longitude: position.coords.longitude }),
        (): void => settle({ reason: DetectFailure.PermissionDenied }),
      );
    });
  }
}
